import { useReducer, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { BasicButton } from '../../../components/Button';
import { Col, Row } from '../../../components/Grid';
import * as T from '../../../components/Typography';
import { LEARNER } from '../../../constants/nav-routes';
import { useNavigate } from 'react-router-dom';
import { progressCheck as validateTpd } from '../../../validation/schemas';
import { progressCheckCmid as validateCmid } from '../../../validation/schemas';
import { BasicSelect } from '../../../components/Inputs';
import { getQuestionsArray } from './Data';
import { useCreateProgressReport } from '../../../api-calls/learners.queries';
import { useAuth } from '../../../context/auth';
import { userRoles, marks } from '../../../constants/data-types';
// import { useGetStepsByMilestoneId } from '../../../api-calls/progressChecks.queries';
import { useGeneralState } from 'context/general-state';
import * as S from './style';
import { useSiteSettings } from 'context/site-settings';

const ProgressProfileQuestions = () => {
  const siteSettings = useSiteSettings();
  const { isWorkingInPrisonEnvironment, isCmidCourse } = siteSettings;
  const validate = isCmidCourse ? validateCmid : validateTpd;

  const questionsArray = getQuestionsArray(siteSettings).filter((q) => {
    if (isWorkingInPrisonEnvironment) {
      return q.showOnProgressReport && !q.hideInPrisonEnvironment;
    }
    return q.showOnProgressReport;
  });

  const { user } = useAuth();
  const { state: generalState } = useGeneralState();
  const { mutateAsync: createProgressReport } = useCreateProgressReport();
  const submitAttempt = useRef(false);
  const { id, milestoneId } = useParams();
  // const { steps } = useGetStepsByMilestoneId({ milestoneId });
  const index = id - 1;
  const key = questionsArray[index].key;
  const navigate = useNavigate();
  const initialState = {
    form: {
      confidentInReadingAbility: '',
      canManageEverydayLife: '',
      communicateWellWithOthers: '',
      feelingCloseToOtherPeople: '',
      ableToReadLetters: '',
      haveGotWithReadingGoals: '',
      optimisticAboutFuture: '',
      happyWithProgress: '',
      education: '',
      employment: '',
      // CMID-specific keys
      dealWithConflict: '',
      confidentInMathsAbility: '',
      budgetManageMoney: '',
      everydayTasksWithNumbers: '',
      mathsGoals: '',
    },
    httpError: '',
    validationErrs: { hasError: false },
    loading: false,
  };
  const [state, setState] = useReducer(reducer, initialState);

  const {
    form: {
      confidentInReadingAbility,
      canManageEverydayLife,
      communicateWellWithOthers,
      feelingCloseToOtherPeople,
      ableToReadLetters,
      haveGotWithReadingGoals,
      optimisticAboutFuture,
      happyWithProgress,
      education,
      employment,
      dealWithConflict,
      confidentInMathsAbility,
      budgetManageMoney,
      everydayTasksWithNumbers,
      mathsGoals,
    },

    validationErrs,
  } = state;

  const setFormData = (value) => {
    setState((prevState) => ({ form: { ...prevState.form, [key]: value } }));
  };

  function reducer(state, newState) {
    let value = newState;
    if (typeof newState === 'function') {
      value = newState(state);
    }
    return { ...state, ...value };
  }

  const validateForm = () => {
    try {
      validate({
        ...state.form,
      });
      setState({ validationErrs: { hasError: false } });
      return true;
    } catch (error) {
      if (error.name === 'ValidationError') {
        setState({
          validationErrs: { ...error.inner, hasError: true },
        });
      }
      return false;
    }
  };
  const addFormData = async () => {
    if (generalState?.isPlayground) return;

    await createProgressReport({
      learnerId:
        user.role === userRoles.COACH ? user.selectedLearnerId : user.id,
      ...state.form,
      milestoneId,
    });
  };

  const handleSubmit = () => {
    if (index === questionsArray.length - 1) {
      submitAttempt.current = true;
      const isValid = validateForm();
      if (isValid) {
        addFormData();
        const params = new URLSearchParams();
        params.set('milestoneId', milestoneId);
        navigate(LEARNER.PROGRESS_CHECK_THANK_YOU + `?${params.toString()}`);
      }
    } else {
      navigate(
        LEARNER.PROGRESS_PROFILE_QUESTIONS.replace(
          ':id',
          Number(id) + 1
        ).replace(':milestoneId', milestoneId)
      );
    }
  };

  useEffect(() => {
    if (id !== 1) {
      navigate(
        LEARNER.PROGRESS_PROFILE_QUESTIONS.replace(':id', 1).replace(
          ':milestoneId',
          milestoneId
        ),
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (submitAttempt.current) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    confidentInReadingAbility,
    canManageEverydayLife,
    communicateWellWithOthers,
    feelingCloseToOtherPeople,
    ableToReadLetters,
    haveGotWithReadingGoals,
    optimisticAboutFuture,
    happyWithProgress,
    education,
    employment,
    dealWithConflict,
    confidentInMathsAbility,
    budgetManageMoney,
    everydayTasksWithNumbers,
    mathsGoals,
  ]);

  const isNextDisabled = () => {
    return !state.form[key];
  };

  return (
    <S.OuterWrapper jc="center">
      <Row jc="center">
        <Col w={[4, 9, 9]} mt="4">
          <T.H2 color="neutralMain">{questionsArray[index].question}</T.H2>
        </Col>
      </Row>
      {questionsArray[index].note && (
        <Row jc="center">
          <Col w={[4, 9, 9]} mt="4">
            <T.P color="neutralMain">{questionsArray[index].note}</T.P>
          </Col>
        </Row>
      )}
      <Row jc="center">
        <Col w={[4, 9, 9]} mt="3">
          {(questionsArray[index].type === 'select' ||
            questionsArray[index].type === 'yesNo') &&
            !questionsArray[index].skip && (
              <BasicSelect
                key={key}
                options={questionsArray[index].options}
                selected={state.form[key]}
                handleChange={(value) => setFormData(value)}
                error={validationErrs[key]}
              />
            )}
          {questionsArray[index].type === 'slider' && (
            <S.StyledSliderInput
              value={state.form[key]}
              onChange={(value) => {
                setFormData(value.target.value);
              }}
              aria-label="Default"
              valueLabelDisplay="auto"
              marks={marks}
              max="10"
            />
          )}
        </Col>
      </Row>

      {questionsArray[index].helper && (
        <Row jc="center">
          <Col w={[4, 9, 9]}>
            <T.P mb={3} mt={3} color="neutral90">
              {questionsArray[index].helper}
            </T.P>
          </Col>
        </Row>
      )}
      {validationErrs.hasError && (
        <Row jc="center">
          <Col w={[4, 9, 9]}>
            <T.P color="error" mb={3}>
              Please go back and fill all the required fields
            </T.P>
          </Col>
        </Row>
      )}
      <S.ButtonWrapper mt="6" style={{ maxWidth: '600px' }} jc="center">
        <Col w={[4, 9, 9]}>
          <BasicButton
            variant="primary"
            icon="next"
            iconProps={{ width: '24px', height: '24px' }}
            handleClick={handleSubmit}
            disabled={isNextDisabled()}
          />
        </Col>
      </S.ButtonWrapper>
    </S.OuterWrapper>
  );
};

export default ProgressProfileQuestions;
